import { Component, Element, h, Host, Method, Prop } from "@stencil/core";

import { setPropsIfNull } from "../../utils/utils";

// we use split button implmentation (calling readpage manually):
// https://wrdev.readspeaker.com/get-started/implementation/split-button-implementation

// we load the library dynamically:
// https://wrdev.readspeaker.com/get-started/implementation/dynamic-loading
function loadLibrary() {
  return new Promise((resolve, reject) => {
    if (window.ReadSpeaker) {
      resolve(window.ReadSpeaker);
      return;
    }

    const oHead = document.getElementsByTagName("HEAD").item(0);
    const oScript = document.createElement("script");

    oScript.type = "text/javascript";
    // oScript.src = "//cdn-eu.readspeaker.com/script/4490/webReader/webReader.js?pids=wr&mobile=0";
    // we disable predefined mobile UI player bar with mobile=0 parameter
    oScript.src = `${window.stzhComponents.pathVendors}/readspeaker/webReader.js?pids=wr&mobile=0`;
    oScript.id = "rs_req_Init";
    oHead.appendChild(oScript);

    oScript.onload = function () {
      window.ReadSpeaker.mobile = window.ReadSpeaker.mobile;
      resolve(window.ReadSpeaker);
      // window.ReadSpeaker.init();
    };

    oScript.onerror = reject;
  });
}

window.rsConf = {
  // cb: {
  // 	ui: {
  // 		open: function() {
  // 			console.log('open')
  // 		},
  //     close: function() {
  // 			console.log('close')
  //     },
  //     stop: function() {
  //       console.log('stop')
  //     },
  //     play: function() {
  //       console.log('play')
  //     },
  //     pause: function() {
  //       console.log('pause')
  //     }
  // 	}
  // },
  settings: {
    // // The color that will be used for highlighting sentences, expressed as a valid CSS color value.
    // hlsent: '#beffd6',
    // // The color that will be used for the text in the highlighted elements, expressed as a valid CSS color value.
    // hltext: '#000000',
    // // The color that will be used for highlighting words, expressed as a valid CSS color value
    // hlword: '#a4cbff'
  },
  general: {
    usePost: true,
    enableSkipAlways: true,
    skipHiddenContent: true,
    shadowDomSelector: "stzh-accordion-item,stzh-timeline-item,stzh-disturber,stzh-clamp",
  },
  ui: {
    tools: {
      voicesettings: false,
      controlpanel: false,
      translation: false,
      dictionary: false,
      download: false,
      help: false,
      formreading: false,
    },
  },
};

// window.rspkr.ui.getActivePlayer().getContainer()
// window.rspkr.ui.addClickEvents()
// window.rspkr.tools.toggler.add()

let readspeakerCounter = 0;

/**
 * @slot - Slot for trigger
 */
@Component({
  tag: "stzh-readspeaker",
  styleUrl: "stzh-readspeaker.scss",
})
export class StzhReadspeaker {
  /** RS url of site (e.g. https://app-eu.readspeaker.com/cgi-bin/rsent?customerid=4490&lang=de_de&readid=content&url=https://www.stadt-zuerich.ch/portal/de/index/service/kontakt.html) */
  @Prop() href!: string;

  @Element() element: HTMLStzhReadspeakerElement;

  /** Open player and start reading page */
  @Method()
  async openPlayer() {
    const activePlayer = window.rspkr?.ui?.getActivePlayer();

    if (activePlayer && !this.element.contains(activePlayer.getContainer())) {
      activePlayer.stop();
    }

    window.readpage(this.href, this.readspeakerId);
  }

  /** Close player and finish reading page */
  @Method()
  async closePlayer() {
    const activePlayer = window.rspkr?.ui?.getActivePlayer();

    if (activePlayer && this.element.contains(activePlayer.getContainer())) {
      activePlayer.close();
    }
  }

  private observer: MutationObserver;
  private bodyObserver: MutationObserver;
  private triggerElement: HTMLElement;

  private onLinkClick = async (event: MouseEvent) => {
    event.preventDefault();
    // library could also be loaded only after first click, but then text selection wouldn't work
    // await loadLibrary();
    this.openPlayer();
  };

  private init = () => {
    let trigger = this.triggerElement?.firstElementChild as HTMLElement;

    if (!trigger) {
      const defaultSlot = Array.from(this.element.children).find(child => !child.hasAttribute("slot")) as HTMLElement;

      trigger = defaultSlot;
    }

    if (trigger) {
      trigger.addEventListener("click", this.onLinkClick);

      setPropsIfNull(trigger, {
        icon: "volume-medium",
        href: this.href,
      } as HTMLStzhLinkElement | HTMLStzhButtonElement);
    }
  };

  /**
   * Initialize a global body mutation observer. Only do that once to prevent
   * multiple observers when having multiple readspeakers.
   */
  private initializeGlobalBodyObserver() {
    if (!window.stzhReadspeakerHasGlobalBodyObserver) {
      window.stzhReadspeakerHasGlobalBodyObserver = true;
      this.bodyObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              this.addClassToAriaHiddenElements(node);
            }
          });
        });
      });
      this.bodyObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Temporary bugfix since readspeaker currently ignores the 'aria-hidden' attribute.
   * Instead we add a class "rs_skip" to it to prevent readspeaker to read those aria-hidden elements.
   */
  private addClassToAriaHiddenElements(node: Node) {
    if (node instanceof HTMLElement) {
      if (
        node.hasAttribute("aria-hidden") &&
        node.getAttribute("aria-hidden") === "true" &&
        !node.classList.contains("rs_skip")
      ) {
        node.classList.add("rs_skip");
      }

      const shadowRoot = node.shadowRoot;
      if (shadowRoot) {
        shadowRoot.childNodes.forEach(childNode => this.addClassToAriaHiddenElements(childNode));
      }
    }
    node.childNodes.forEach(childNode => this.addClassToAriaHiddenElements(childNode));
  }

  private readspeakerId: string;

  async componentWillLoad() {
    this.readspeakerId = `stzh-readspeaker-${readspeakerCounter++}`;
  }

  async componentDidLoad() {
    this.addClassToAriaHiddenElements(document.body);
    this.initializeGlobalBodyObserver();
    await loadLibrary();
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.bodyObserver) {
      this.bodyObserver.disconnect();
    }

    window.rspkr?.ui?.destroyActivePlayer();
  }

  render() {
    const classes = {
      "stzh-readspeaker": true,
      rs_skip: true, // will prevent component being read by readspeaker
    };

    return (
      <Host>
        <div class={classes}>
          <div class="stzh-readspeaker__actions">
            <div ref={el => (this.triggerElement = el as HTMLDivElement)} class="stzh-readspeaker__trigger">
              <slot></slot>
            </div>
            <stzh-button
              class="stzh-readspeaker__close"
              variant="tertiary"
              size="small"
              icon="close-big"
              iconOnly={true}
              onClick={() => {
                this.closePlayer();
              }}
            ></stzh-button>
          </div>

          {/*
          <stzh-popover placement="bottom-end">
            <div slot="content">...</div>
          </stzh-popover>
          */}

          <div id={this.readspeakerId} class="rs_addtools rs_splitbutton rs_preserve rs_skip rs_exp"></div>
        </div>
      </Host>
    );
  }
}
